import React, { useState, useEffect, useRef } from 'react';
import { Oval, ThreeDots, Rings } from 'react-loader-spinner';
import { gsap } from 'gsap';
import styles from '../styles/components/_Loader.module.scss';

const Loader = ({ type = 'rings', isPageLoading }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [resourcesLoaded, setResourcesLoaded] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [canRemove, setCanRemove] = useState(false);
  
  const spinnerWrapperRef = useRef(null);
  const loaderRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const checkResources = async () => {
      try {
        await document.fonts.ready;
        const images = Array.from(document.images);
        await Promise.all(
          images.map(img => {
            return new Promise((resolve) => {
              if (img.complete) {
                resolve();
              } else {
                img.onload = resolve;
                img.onerror = resolve;
              }
            });
          })
        );
        setResourcesLoaded(true);
      } catch (error) {
        console.error('Erreur lors du chargement des ressources:', error);
        setResourcesLoaded(true);
      }
    };
    checkResources();

    // Cleanup des animations en cours
    return () => {
      if (animationRef.current) {
        animationRef.current.kill();
      }
    };
  }, []);

  useEffect(() => {
    if (resourcesLoaded && !isPageLoading) {
      const minLoadingTime = 150;
      const timer = setTimeout(() => {
        handleExit();
      }, minLoadingTime);

      return () => clearTimeout(timer);
    }
  }, [resourcesLoaded, isPageLoading]);

  const handleExit = () => {
    if (!spinnerWrapperRef.current || !loaderRef.current) return;

    setIsExiting(true);

    // Timeline pour coordonner les animations
    const tl = gsap.timeline({
      onComplete: () => {
        setCanRemove(true);
        setTimeout(() => {
          setIsLoading(false);
        }, 50);
      }
    });

    // Stocker la référence de la timeline pour le cleanup
    animationRef.current = tl;

    // Première phase
    tl.to(spinnerWrapperRef.current, {
      scale: 1.5,
      rotation: 720,
      duration: 0.8,
      ease: "power2.inOut"
    })
    // Deuxième phase
    .to(spinnerWrapperRef.current, {
      scale: 0,
      rotation: 1080,
      duration: 0.4,
      ease: "power4.in"
    }, "+=0")
    // Animation du background
    .to(loaderRef.current, {
      opacity: 0,
      duration: 0.5,
      ease: "power2.inOut"
    }, "-=0.2");
  };

  // Si le loading est terminé, ne rien rendre
  if (!isLoading) return null;

  const getLoader = () => {
    const commonProps = {
      height: 80,
      width: 80,
      className: `${styles.spinner} ${isExiting ? styles.exiting : ''}`,
      color: "var(--white)"
    };

    switch(type) {
      case 'oval':
        return <Oval {...commonProps} strokeWidth={5} strokeWidthSecondary={4} />;
      case 'dots':
        return <ThreeDots {...commonProps} radius={9} />;
      default:
        return <Rings {...commonProps} radius={6} />;
    }
  };

  return (
    <div 
      ref={loaderRef}
      className={`${styles.loader} ${isExiting ? styles.exit : ''} ${canRemove ? styles.remove : ''}`}
    >
      <div 
        ref={spinnerWrapperRef} 
        className={styles.spinnerWrapper}
      >
        {getLoader()}
      </div>
    </div>
  );
};

export default Loader;